import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";

/**
 * HIBANE Typography Primitives
 * Single source of truth for all text in the app.
 * Built on the Bottom Tab DNA (system-ui stack, medium weight, tight tracking).
 * ESLint guard (local/no-raw-typography) blocks raw text- and font- utilities on
 * h1-h6/p/span/small outside this file.
 */

/* -------- Heading -------------------------------------------------------- */

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

const HEADING_CLASSES: Record<HeadingLevel, string> = {
  1: "text-[length:var(--text-4xl)] font-[600] leading-[1.15] tracking-[var(--tracking-tight)]",
  2: "text-[length:var(--text-3xl)] font-[600] leading-[1.15] tracking-[var(--tracking-tight)]",
  3: "text-[length:var(--text-2xl)] font-[600] leading-[1.3] tracking-[var(--tracking-tight)]",
  4: "text-[length:var(--text-xl)]  font-[500] leading-[1.3] tracking-[var(--tracking-tight)]",
  5: "text-[length:var(--text-lg)]  font-[500] leading-[1.3] tracking-[var(--tracking-tight)]",
  6: "text-[length:var(--text-md)]  font-[500] leading-[1.3] tracking-[var(--tracking-tight)]",
};

export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  level?: HeadingLevel;
  /** Visually render as a different level while keeping semantic tag. */
  visualLevel?: HeadingLevel;
  asChild?: boolean;
}

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ level = 2, visualLevel, className, asChild, ...props }, ref) => {
    const Tag = (`h${level}`) as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
    const Comp = asChild ? Slot : (Tag as React.ElementType);
    const styleLevel = visualLevel ?? level;
    return (
      <Comp ref={ref} className={cn(HEADING_CLASSES[styleLevel], className)} {...props} />
    );
  },
);
Heading.displayName = "Heading";

/* -------- Text ----------------------------------------------------------- */

type TextSize = "2xs" | "xs" | "sm" | "md" | "lg" | "xl";
type TextWeight = "regular" | "medium" | "semibold" | "bold";
type TextTone = "default" | "muted" | "inverse" | "accent" | "danger";

const SIZE_CLASS: Record<TextSize, string> = {
  "2xs": "text-[length:var(--text-2xs)] leading-[1.3]",
  xs: "text-[length:var(--text-xs)] leading-[1.3]",
  sm: "text-[length:var(--text-sm)] leading-[1.45]",
  md: "text-[length:var(--text-md)] leading-[1.5]",
  lg: "text-[length:var(--text-lg)] leading-[1.5]",
  xl: "text-[length:var(--text-xl)] leading-[1.4]",
};

const WEIGHT_CLASS: Record<TextWeight, string> = {
  regular: "font-[400]",
  medium: "font-[500]",
  semibold: "font-[600]",
  bold: "font-[700]",
};

const TONE_CLASS: Record<TextTone, string> = {
  default: "text-foreground",
  muted: "text-muted-foreground",
  inverse: "text-white",
  accent: "text-cyan-600",
  danger: "text-destructive",
};

export interface TextProps extends React.HTMLAttributes<HTMLElement> {
  size?: TextSize;
  weight?: TextWeight;
  tone?: TextTone;
  truncate?: boolean;
  as?: "p" | "span" | "div" | "li" | "small";
  asChild?: boolean;
}

export const Text = React.forwardRef<HTMLElement, TextProps>(
  (
    {
      size = "md",
      weight = "medium",
      tone = "default",
      truncate,
      as = "p",
      asChild,
      className,
      ...props
    },
    ref,
  ) => {
    const Comp: React.ElementType = asChild ? Slot : as;
    return (
      <Comp
        ref={ref as React.Ref<HTMLElement>}
        className={cn(
          "tracking-[var(--tracking-tight)]",
          SIZE_CLASS[size],
          WEIGHT_CLASS[weight],
          TONE_CLASS[tone],
          truncate && "truncate",
          className,
        )}
        {...props}
      />
    );
  },
);
Text.displayName = "Text";

/* -------- Label / Caption / ButtonText / CardTitle / CardDescription ----- */

export const Label = React.forwardRef<HTMLLabelElement, React.LabelHTMLAttributes<HTMLLabelElement>>(
  ({ className, ...props }, ref) => (
    <label
      ref={ref}
      className={cn(
        "text-[length:var(--text-sm)] font-[500] leading-[1.3] tracking-[var(--tracking-tight)] text-foreground",
        className,
      )}
      {...props}
    />
  ),
);
Label.displayName = "Label";

export const Caption = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        "text-[length:var(--text-xs)] font-[500] leading-[1.3] tracking-[var(--tracking-tight)] text-muted-foreground",
        className,
      )}
      {...props}
    />
  ),
);
Caption.displayName = "Caption";

export const ButtonText = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(
        "text-[length:var(--text-sm)] font-[600] leading-[1.2] tracking-[var(--tracking-tight)]",
        className,
      )}
      {...props}
    />
  ),
);
ButtonText.displayName = "ButtonText";

export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        "text-[length:var(--text-xl)] font-[600] leading-[1.3] tracking-[var(--tracking-tight)] text-foreground",
        className,
      )}
      {...props}
    />
  ),
);
CardTitle.displayName = "CardTitle";

export const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn(
        "text-[length:var(--text-sm)] font-[500] leading-[1.45] tracking-[var(--tracking-tight)] text-muted-foreground",
        className,
      )}
      {...props}
    />
  ),
);
CardDescription.displayName = "CardDescription";
